import React, { memo, useEffect, useCallback, useMemo, useReducer } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, shallowEqual } from 'react-redux'
import { createSelector } from 'reselect'
import {
  Tooltip,
  Typography,
  Button,
  ButtonBase,
  Fab,
  makeStyles,
  List,
  ListItem,
  Grid,
  Divider,
  Avatar,
  CircularProgress,
} from '@material-ui/core'
import IconStore from '@material-ui/icons/Store'
import IconVerifiedUser from '@material-ui/icons/VerifiedUser'
import IconLocation from '@material-ui/icons/LocationOn'
import InfoIcon from '@material-ui/icons/Info'
import IconDirections from '@material-ui/icons/Directions'
import IconPhoto from '@material-ui/icons/InsertPhoto'

import DialogReportProblem, { useDialogReport } from './DialogReportProblem'

import StoreContacts from '~/components/StoreContacts'
import ProductImageThumb from '~/components/ProductImageThumb'
import WhatsIcon from '~/components/WhatsIcon'
import { useLiterals } from '~/components/Literals'
import { getExtraInfoByBrand } from '~/components/StoreList/helper'
import { formatDistance, getWhatsAppUrl } from '~/utils'
import { mountUrl, replaceImagePath, parseStatsToProducts } from '~/utils'

import api from '~/api'
import track from '~/track'

import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'

const selector = createSelector(
  (state) => state.app.currentStore,
  (state) => state.app.currentProduct,
  (...args) => args,
)

const useStyles = makeStyles((theme) => ({
  bar: {
    height: 500,
    width: '100%',
    overflow: 'auto',
  },
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  loader: {
    position: 'absolute',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  detail: ({ config }) => ({
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 54,
    borderBottom: !config.showContextViewStore ? '1px solid #ccc' : '',
    '& svg': {
      marginRight: 8,
    },
    '&:last-child': {
      justifyContent: 'center',
      height: '100%',
      '& button': {
        alignSelf: 'flex-end',
      },
    },
  }),
  phone: {
    '&:hover': { cursor: 'pointer' },
  },
  header: {
    position: 'relative',
    padding: 16,
    background: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText,
    display: 'flex',
    justifyContent: 'center',
    '& > div:first-child': {
      width: 'calc(100% - 56px)',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    [theme.breakpoints.up('md')]: {
      height: 'auto',
    },
  },
  buttonRoute: {
    width: 56,
    background: '#fff',
    color: '#666',
    zIndex: 1200,
  },
  stock: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: '8px 16px',
  },
  buttonsFooter: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-around',
    },
    padding: 16,
  },

  whatsButton: {
    transition: 'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    backgroundColor: '#25D366',
    color: '#fff',
    '&:hover': {
      opacity: 0.9,
      backgroundColor: '#25D366',
      color: '#fff',
    },
  },
}))

function reducer(state, action) {
  return { ...state, ...action }
}

function StoreDetails({ config, handleToggle, position }) {
  const classes = useStyles({ config })
  const history = useHistory()
  const { getLiteral } = useLiterals()

  const [store, product] = useSelector(selector, shallowEqual)
  const [state, dispatch] = useReducer(reducer, { products: null, loadingProducts: true })

  const { products, loadingProducts } = state
  const { place, loading, name, cnpj, coordinates } = store
    ? store
    : { place: null, loading: null, name: null, cnpj: null, coordinates: null }

  const dialogReport = useDialogReport(store, product, position)

  const extraInfo = useMemo(
    () => store?.locatorExtraInfo && getExtraInfoByBrand(store.locatorExtraInfo, config.cnpj),
    [store, config.cnpj],
  )

  const log = (data) => {
    const logParams = {
      name,
      cnpj,
      suppliers: store.suppliers,
      products: parseStatsToProducts(store.stats),
      brand: config.isOndetem ? product.brand : null,
      user: position?.user,
      ...data,
    }

    return api.clickTrack(logParams)
  }

  useEffect(() => {
    log({ event: 'store' })
    handleToggle()
  }, [cnpj])

  const handleGoRoute = () => {
    if (place) {
      window.open(place.url)
    } else {
      window.open(`http://maps.google.com/?q=${coordinates.formattedAddress}`)
    }
    track('cliqueRota', { category: 'menu', name: store.name })
    log({ event: 'route' })
  }

  const handleClaim = () => {
    window.open('https://varejo.gofind.com.br/')
    track('reivindicarLoja', { category: 'loja', name: store.name })
    log({ event: 'claim' })
  }

  // agora só faz track ;)
  const handleMoreProducts = useCallback(() => {
    history.push(`/loja/${btoa(store.cnpj)}/produtos`, {
      fromApp: true,
    })

    track('verMaisProdutos', { category: 'loja', name: store.name })
    log({ event: 'seeMoreProducts' })
  }, [store])

  const handleSeePhone = (type) => log({ event: 'call', type })

  useEffect(() => {
    async function getProducts() {
      const response = await api.getStoreProducts(cnpj)

      dispatch({
        products: response.products,
        loadingProducts: false,
      })
    }

    getProducts()
  }, [cnpj])

  return (
    <>
      <DialogReportProblem {...dialogReport} />
      <div className={classes.wrapper}>
        <div className={classes.header}>
          <div>
            <Typography variant="subtitle1" color="inherit">
              {store.name}
            </Typography>
            {place && place.opening_hours && (
              <Typography variant="body1" color="inherit">
                {place.opening_hours.open_now ? getLiteral('2090') : getLiteral('2091')}
              </Typography>
            )}
            <Typography variant="body1" color="inherit">
              {formatDistance(store.distance)}
            </Typography>
          </div>
          <Tooltip title={getLiteral('1479')}>
            <Fab
              aria-label="Route"
              className={classes.buttonRoute}
              onClick={handleGoRoute}
              disabled={loading}
              data-cy="route"
              data_analytics_category="onde-comprar"
              data_analytics_action="get_directions"
              data_analytics_partner_name={store?.name}
              data_analytics_partner_address={store?.coordinates?.formattedAddress}
              data_analytics_product_id={product?.gtin}
              data_analytics_product_name={product?.name}
              data_analytics_product_size={product?.name}
              data_analytics_product_type={product?.name}
              data_analytics_local={position?.user?.city}
            >
              <IconDirections />
            </Fab>
          </Tooltip>
        </div>

        {store.stockMessage ||
          (store.featured && (
            <div className={classes.stock}>
              <Typography variant="body2" color="inherit">
                {store.stockMessage || store.featured.message}
              </Typography>
            </div>
          ))}

        <div className={classes.details}>
          <div className={classes.detail}>
            <IconLocation color="primary" />
            <Typography variant="body1" color="inherit" data-cy="address">
              {store.coordinates.formattedAddress}
            </Typography>
          </div>

          {extraInfo && (
            <div className={classes.detail}>
              <InfoIcon color="primary" />
              <Typography variant="body1" color="inherit" data-cy="extraInfo">
                {extraInfo}
              </Typography>
            </div>
          )}

          <StoreContacts
            color="primary"
            whatsapp={store.whatsapp}
            phoneNumber={store.phoneNumber}
            classDetail={classes.detail}
            onSeePhone={handleSeePhone}
            storeName={store.name}
          />

          {!store.verified ? (
            !config.hideClaimStore && (
              <>
                <ButtonBase component="a" className={classes.detail} onClick={handleClaim} data-cy="claim">
                  <IconStore color="primary" />
                  <Typography variant="body1" color="inherit">
                    {getLiteral('2004')}
                  </Typography>
                </ButtonBase>
                {config.showContextViewStore && <div />}
              </>
            )
          ) : (
            <>
              <div className={classes.detail}>
                <IconVerifiedUser color="primary" />
                <Typography variant="body1" color="inherit">
                  {getLiteral('2005')}
                </Typography>
              </div>
              {config.showContextViewStore && <div />}
            </>
          )}

          {config.multiProduct && (
            <List>
              {Object.entries(store.stats).map(([key, value]) => (
                <ListItem key={key}>
                  <ProductImageThumb product={value} />
                  <Typography style={{ marginLeft: 8 }} variant="button">
                    {value.name}
                    <br />
                    <Typography variant="body2" color="secondary" style={{ textTransform: 'initial' }}></Typography>
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}

          {config.showContextViewStore && (
            <>
              <Grid style={{ padding: '8px 16px' }}>
                <Divider style={{ marginBottom: 16 }} />
                <Typography variant="body1" style={{ fontSize: 13, fontWeight: 500 }}>
                  Você pode encontrar esses produtos nesse estabelecimento:
                </Typography>
              </Grid>

              {loadingProducts && (
                <Grid style={{ margin: '80px auto', textAlign: 'center' }}>
                  <CircularProgress color="primary" />
                  <Typography variant="body1">Carregando mais items...</Typography>
                </Grid>
              )}

              {!loadingProducts && (
                <SimpleBar className={classes.bar}>
                  <List id="product-list-by-store">
                    {products?.map((product) => {
                      const image = replaceImagePath(product)

                      return (
                        <ListItem key={product.gtin}>
                          {image ? (
                            <img
                              src={mountUrl(image, { height: 100 })}
                              width={60}
                              height={60}
                              alt={product.description}
                              style={{ border: '2px solid #c4c4c4', padding: 4 }}
                            />
                          ) : (
                            <Avatar>
                              <IconPhoto />
                            </Avatar>
                          )}
                          <Typography variant="body1" style={{ marginLeft: 8 }}>
                            {product.name}
                          </Typography>
                        </ListItem>
                      )
                    })}
                  </List>
                </SimpleBar>
              )}
            </>
          )}

          {!config.showContextViewStore && (
            <div className={classes.buttonsFooter}>
              <div className={classes.buttons}>
                {!config.hideShowMoreProducts && (
                  <Button
                    size="large"
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleMoreProducts}
                    style={{ minHeight: 38, marginBottom: 16 }}
                    data-cy="seeMoreProducts"
                  >
                    {getLiteral('2003')}
                  </Button>
                )}
                {store.whatsapp && store.whatsapp.trim() && (
                  <Button
                    className={classes.whatsButton}
                    href={getWhatsAppUrl(
                      store.whatsapp,
                      name,
                      product?.name ||
                        Object.values(store.stats)
                          .map((i) => i.name)
                          .join(),
                      config.brandName,
                      config.showContextViewEstablishment,
                      getLiteral,
                      config?.textToWhatsapp,
                    )}
                    target="_blank"
                    component="a"
                    fullWidth
                    size="large"
                    onClick={() => track('cliqueWhats', { category: 'loja', name: store.name })}
                  >
                    <WhatsIcon color="inherit" style={{ marginRight: 8 }} /> {getLiteral('2092')}
                  </Button>
                )}
              </div>
              <br />
              <br />
              <Button
                size="small"
                fullWidth
                align="center"
                component="div"
                onClick={dialogReport.actions.handleOpen}
                style={{ alignSelf: 'flex-end' }}
              >
                {getLiteral('2088')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default memo(StoreDetails)
